// Copyright 2025, University of Maryland, All Rights Reserved
import { Button } from '@/components/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  showing: number
  totalItems: number
  onPageChange: (page: number) => void
}

export function PaginationControls({
  currentPage,
  totalPages,
  showing,
  totalItems,
  onPageChange,
}: PaginationControlsProps): JSX.Element {
  if (totalPages <= 1) {
    return <></>
  }

  const goToFirst = () => onPageChange(1)
  const goToPrev = () => onPageChange(Math.max(1, currentPage - 1))
  const goToNext = () => onPageChange(Math.min(totalPages, currentPage + 1))
  const goToLast = () => onPageChange(totalPages)

  const renderPageButtons = (maxVisible: number) => {
    const buttons: JSX.Element[] = []
    const visible = Math.min(maxVisible, totalPages)

    for (let index = 0; index < visible; index += 1) {
      let pageNum: number
      if (totalPages <= visible) {
        pageNum = index + 1
      } else if (currentPage <= Math.ceil(visible / 2)) {
        pageNum = index + 1
      } else if (currentPage >= totalPages - Math.floor(visible / 2)) {
        pageNum = totalPages - visible + 1 + index
      } else {
        pageNum = currentPage - Math.floor(visible / 2) + index
      }

      buttons.push(
        <Button
          key={pageNum}
          variant={currentPage === pageNum ? 'default' : 'outline'}
          size="sm"
          className="h-8 w-8 p-0 text-xs sm:h-9 sm:w-9 sm:text-sm"
          onClick={() => onPageChange(pageNum)}
        >
          {pageNum}
        </Button>,
      )
    }

    return buttons
  }

  return (
    <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between sm:gap-4" data-testid="kb-pagination">
      <div className="flex items-center gap-1">
        {/* First/last buttons hidden on mobile to save space */}
        <Button aria-label="First page" variant="outline" size="sm" className="hidden sm:inline-flex" onClick={goToFirst} disabled={currentPage === 1}>
          <ChevronLeft className="h-4 w-4" />
          <ChevronLeft className="h-4 w-4 -ml-2" />
        </Button>
        <Button aria-label="Previous page" variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3" onClick={goToPrev} disabled={currentPage === 1}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {/* 3 page buttons on mobile, 5 on desktop */}
        <div className="flex items-center gap-1 sm:hidden">
          {renderPageButtons(3)}
        </div>
        <div className="hidden sm:flex items-center gap-1">
          {renderPageButtons(5)}
        </div>
        <Button aria-label="Next page" variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3" onClick={goToNext} disabled={currentPage === totalPages}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button aria-label="Last page" variant="outline" size="sm" className="hidden sm:inline-flex" onClick={goToLast} disabled={currentPage === totalPages}>
          <ChevronRight className="h-4 w-4" />
          <ChevronRight className="h-4 w-4 -ml-2" />
        </Button>
      </div>
      <div className="text-xs text-muted-foreground sm:text-sm">
        Page {currentPage} of {totalPages} · {showing} of {totalItems} items
      </div>
    </div>
  )
}
